import React, { useEffect, useState } from 'react'

import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import Heading from '@react/react-spectrum/Heading'
import Help from '@react/react-spectrum/Icon/Help'
import NumberInput from '@react/react-spectrum/NumberInput'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Select from '@react/react-spectrum/Select'
import Switch from '@react/react-spectrum/Switch'
import Tooltip from '@react/react-spectrum/Tooltip'

import ScreenPicker from 'components/ScreenPicker'
import { useProject } from 'hooks'

let saveImmediately = false

function NavigationForm() {
  const { project, type, updateProjectPart, config, canEdit } = useProject()
  const [settings, setSettings] = useState(project.meta)
  const [screens, setScreens] = useState(project.screens)
  const [shouldSaveSettings, setShouldSaveSettings] = useState(false)
  const [shouldSaveScreens, setShouldSaveScreens] = useState(false)

  const navigation = settings?.navigation || {}

  useEffect(() => {
    if (settings && shouldSaveSettings && canEdit) {
      updateProjectPart('meta', settings, saveImmediately)
    }
    saveImmediately = false
  }, [settings, shouldSaveSettings, canEdit])

  useEffect(() => {
    if (screens && shouldSaveScreens && canEdit) {
      updateProjectPart('screens', screens, saveImmediately)
    }
    saveImmediately = false
  }, [screens, shouldSaveScreens, canEdit])

  const onChange = (propName) => (value) => {
    if (canEdit) {
      setShouldSaveSettings(true)
      setSettings((s) => ({ ...s, [propName]: value }))
    }
  }

  const onNavigationChange = (propName) => (value) => {
    if (canEdit) {
      setShouldSaveSettings(true)
      setSettings((s) => ({ ...s, navigation: { ...(s.navigation || {}), [propName]: value } }))
    }
  }

  const onScreensChange = (screens) => {
    if (canEdit) {
      setShouldSaveScreens(true)
      setScreens(screens)
    }
  }

  // read navigationScreens from meta for web-modular, fall back to reading from individual screens
  const menu =
    (['web-modular', 'customer-hub', 'web-social'].includes(type) && navigation.menu) ||
    Object.values(screens)
      .filter((s) => s.meta.includeInNavigation)
      .sort((a, b) => a.meta.navigationOrder - b.meta.navigationOrder)
      .map((s) => s.meta.name)

  // read rootScreen and authoriedRootScreen from meta or fall back to reading from individual screens
  const rootScreen =
    (['web-modular'].includes(type) && navigation.rootScreen) ||
    Object.values(screens).find((s) => s.meta.isRootScreen)?.meta?.name
  const authorizedRootScreen =
    (['web-modular'].includes(type) && navigation.authorizedRootScreen) ||
    Object.values(screens).find((s) => s.meta.isAuthorizedRootScreen)?.meta?.name

  const navOptions = config.navigationAlignmentOptions
  const navigationAlignment =
    navOptions &&
    (navigation.alignment ||
      settings.navigationAlignment ||
      navOptions.find((opt) => opt.default)?.value ||
      navOptions[0]?.value)

  const logoPositionOptions = config.logoPositionOptions
  const logoPosition =
    logoPositionOptions &&
    (navigation.logoPosition ||
      settings.logoPosition ||
      logoPositionOptions.find((opt) => opt.default)?.value ||
      logoPositionOptions[0]?.value)

  const defaultCartStorage = config.cartStorageOptions?.find((o) => o.default)?.value || 'local'

  const updateNavigationScreens = (names) => {
    saveImmediately = true
    onNavigationChange('menu')(names)

    const areAnchorsIncluded = names.some((n) => typeof n?.anchor !== 'function' && !!n?.anchor)

    // don't execute the backward-compatibility code if anchors are included
    if (!areAnchorsIncluded) {
      // backward-compatibility
      const namesAsStrings = names.map((n) => (typeof n === 'string' ? n : n?.fullValue || n?.value || ''))
      let updatedScreens = Object.values(screens)
        .map((s) => ({
          ...s,
          meta: { ...s.meta, includeInNavigation: false, navigationOrder: null },
        }))
        .reduce((acc, s) => ({ ...acc, [s.meta.name]: s }), {})

      namesAsStrings.forEach((n, idx) => {
        const [name] = n.split('#')
        updatedScreens[name].meta.includeInNavigation = true
        updatedScreens[name].meta.navigationOrder = idx + 1
      })
      onScreensChange(updatedScreens)
    }
  }

  const updateRootScreen = (name) => {
    saveImmediately = true
    onNavigationChange('rootScreen')(name)

    // backward-compatibility
    const updatedScreens = Object.values(screens)
      .map((s) => ({
        ...s,
        meta: { ...s.meta, isRootScreen: name === s.meta.name },
      }))
      .reduce((acc, s) => ({ ...acc, [s.meta.name]: s }), {})
    onScreensChange(updatedScreens)
  }

  const updateAuthorizedRootScreen = (name) => {
    saveImmediately = true
    onNavigationChange('authorizedRootScreen')(name)

    // backward-compatibility
    const updatedScreens = Object.values(screens)
      .map((s) => ({
        ...s,
        meta: { ...s.meta, isAuthorizedRootScreen: name === s.meta.name },
      }))
      .reduce((acc, s) => ({ ...acc, [s.meta.name]: s }), {})
    onScreensChange(updatedScreens)
  }

  const updateNavigationAlignment = (v) => {
    onNavigationChange('alignment')(v)

    // backward-compatibility
    onChange('navigationAlignment')(v)
  }

  const updateLogoPosition = (v) => {
    onNavigationChange('logoPosition')(v)

    // backward-compatibility
    onChange('logoPosition')(v)
  }

  const updateTopRightLink = (v) => {
    onNavigationChange('topRightLink')(v)

    // backward-compatibility
    onChange('topRightLink')
  }

  // Gets field value or fallback if undefined or null. 0, false and empty string are considered valid values
  const getValue = (name, fallback) => {
    if (navigation[name] !== undefined && navigation[name] !== null) return navigation[name]
    if (settings[name] !== undefined && settings[name] !== null) return settings[name]
    return fallback
  }

  return (
    <div className='NavigationForm'>
      <section>
        <Heading size='4'>Screens</Heading>

        {['web', 'web-modular'].includes(type) && (
          <FieldLabel label='Home screen'>
            <ScreenPicker value={rootScreen} onChange={updateRootScreen} disabled={!canEdit} />
          </FieldLabel>
        )}

        <FieldLabel label='Navigation Screens'>
          <ScreenPicker
            value={menu}
            onChange={updateNavigationScreens}
            disabled={!canEdit}
            limit={config.navigationLimit}
            multiple
            allowAnchors
            shouldFormatAnchors={['customer-hub', 'web-modular', 'web-social'].includes(type)}
            allowDuplicates
          />
        </FieldLabel>

        {['mobile'].includes(type) && (
          <FieldLabel label='Top right link'>
            <ScreenPicker
              value={navigation.topRightLink || settings.topRightLink}
              onChange={updateTopRightLink}
              disabled={!canEdit}
            />
            <OverlayTrigger placement='right'>
              <Button className='tooltip-btn' variant='tool' icon={<Help />} />
              <Tooltip>Icon for this button can be set in Screens tab, in selected screen's sidebar.</Tooltip>
            </OverlayTrigger>
          </FieldLabel>
        )}
      </section>

      {['web-modular', 'mobile'].includes(type) && (
        <section>
          <Heading size='4'>Authentication</Heading>

          <FieldLabel label='Authentication' labelFor='authentication'>
            <Select
              id='authentication'
              value={settings.authentication}
              options={config.authOptions}
              onChange={onChange('authentication')}
              disabled={!canEdit}
            />
          </FieldLabel>

          {['web-modular'].includes(type) && (
            <>
              <FieldLabel label='Signed-in home screen'>
                <ScreenPicker
                  value={authorizedRootScreen}
                  onChange={updateAuthorizedRootScreen}
                  disabled={!canEdit || !['optional', 'always'].includes(settings.authentication)}
                />
                <OverlayTrigger placement='right'>
                  <Button className='tooltip-btn' variant='tool' icon={<Help />} />
                  <Tooltip>Screen to be used as a home page for a signed-in user.</Tooltip>
                </OverlayTrigger>
              </FieldLabel>

              <FieldLabel label='Signed-in navigation screens'>
                <ScreenPicker
                  value={navigation.authorizedMenu || menu}
                  onChange={onNavigationChange('authorizedMenu')}
                  disabled={!canEdit || !['optional', 'always'].includes(settings.authentication)}
                  limit={config.navigationLimit}
                  multiple
                  allowAnchors
                  shouldFormatAnchors={['customer-hub', 'web-modular', 'web-social'].includes(type)}
                  allowDuplicates
                />
              </FieldLabel>
            </>
          )}
        </section>
      )}

      <section>
        {['web', 'web-modular', 'customer-hub', 'web-social'].includes(type) && (
          <>
            <Heading size='4'>Layout</Heading>
            {navOptions && (
              <FieldLabel label='Horizontal Alignment'>
                <Select
                  options={navOptions}
                  value={navigationAlignment}
                  onChange={updateNavigationAlignment}
                  disabled={!canEdit}
                />
              </FieldLabel>
            )}

            {['web-modular'].includes(type) && logoPositionOptions && (
              <FieldLabel label='Logo position'>
                <Select
                  options={logoPositionOptions}
                  value={logoPosition}
                  onChange={updateLogoPosition}
                  disabled={!canEdit}
                />
              </FieldLabel>
            )}

            {['web-modular', 'customer-hub', 'web-social'].includes(type) && (
              <FieldLabel label='Navigation visibility'>
                <Select
                  id='authentication'
                  value={settings.navigationVisibility || 'alwaysVisible'}
                  options={config.navigationVisibilityOptions}
                  onChange={onChange('navigationVisibility')}
                  disabled={!canEdit}
                />
              </FieldLabel>
            )}

            {settings.navigationVisibility === 'visibleOnScroll' && (
              <FieldLabel label='Scroll threshold (px)'>
                <NumberInput
                  min={0}
                  max={2000}
                  name='navigationScrollThreshold'
                  value={settings.navigationScrollThreshold}
                  onChange={onChange('navigationScrollThreshold')}
                  disabled={!canEdit}
                />
              </FieldLabel>
            )}

            <br />
          </>
        )}

        {['web', 'web-modular', 'mobile-v2'].includes(type) && <Heading size='4'>Sign-in and Registration</Heading>}

        {['web'].includes(type) && (
          <FieldLabel label='Registration fields' labelFor='registrationFields'>
            <Select
              options={[
                { label: 'Retail', value: 'retail' },
                { label: 'Grocery', value: 'grocery' },
                { label: 'News', value: 'news' },
                { label: 'Telco', value: 'telco' },
              ]}
              value={settings.registrationFields || 'retail'}
              id='registrationFields'
              onChange={onChange('registrationFields')}
              disabled={!canEdit}
            />
          </FieldLabel>
        )}

        {['web-modular', 'mobile-v2'].includes(type) && (
          <>
            <FieldLabel label='Registration screen'>
              <ScreenPicker
                value={getValue('registrationScreen', 'register')}
                onChange={onNavigationChange('registrationScreen')}
                disabled={!canEdit}
                allowExternal={type === 'web-modular'}
              />
            </FieldLabel>
            <FieldLabel label='Login screen'>
              <ScreenPicker
                value={getValue('loginScreen', 'sign-in')}
                onChange={onNavigationChange('loginScreen')}
                disabled={!canEdit}
                allowExternal={type === 'web-modular'}
              />
            </FieldLabel>
            <FieldLabel label='Profile screen'>
              <ScreenPicker
                value={getValue('profileScreen', 'profile')}
                onChange={onNavigationChange('profileScreen')}
                disabled={!canEdit}
                allowExternal={type === 'web-modular'}
              />
            </FieldLabel>
          </>
        )}

        {['web-modular'].includes(type) && (
          <Switch
            checked={settings.resetEcidOnSignOut !== false}
            onChange={(v) => canEdit && onChange('resetEcidOnSignOut')(v)}
            label='Reset ECID on sign out'
          />
        )}

        {['web-modular', 'mobile', 'mobile-v2'].includes(type) && (
          <>
            <Heading size='4'>Cart</Heading>
            <Switch
              checked={settings.useCart}
              onChange={(v) => canEdit && onChange('useCart')(v)}
              label={'Enable cart'}
            />
          </>
        )}

        {['web-modular'].includes(type) && settings.useCart && (
          <FieldLabel label='Cart screen'>
            <ScreenPicker value={settings.cartScreen || 'cart'} onChange={onChange('cartScreen')} disabled={!canEdit} />
          </FieldLabel>
        )}
        {['web-modular', 'mobile'].includes(type) && settings.useCart && config.cartStorageOptions?.length > 0 && (
          <FieldLabel label='Cart Storage' labelFor='cartStorage'>
            <Select
              options={config.cartStorageOptions}
              value={settings.cartStorage || defaultCartStorage}
              id='cartStorage'
              onChange={onChange('cartStorage')}
              disabled={!canEdit}
            />
          </FieldLabel>
        )}
      </section>
    </div>
  )
}

export default NavigationForm
